import { flatten, uniqBy } from 'lodash';

import type { OptionToggle } from '@swf/components/Inputs/components/MultipleSelect/MultipleSelectToggle/MultipleSelectToggle';
import type { Game, Speed, Structure } from '@swf/generated/graphql/poker/MTT/schemas';
import type {
  FilterNameType,
  FilterOption,
  FilterOptionBase,
  FilterOptionGroup,
  FilterVariables
} from '@swf/poker';
import {
  FilterName,
  FilterOptionBuyIn,
  FilterOptionGame,
  FilterOptionSpeed,
  FilterOptionTableSize
} from '@swf/poker';

import {
  FILTER_OPTION_VALUE_CUSTOM,
  FILTER_RANGE_DEFAULT,
  FILTER_RANGE_MULTIPLIER
} from '../constants';
import type {
  ActiveUiFilters,
  CustomRangeBuyIn,
  CustomRangePayload,
  FilterOptionVariableMap,
  FiltersBuyInValue,
  PlayMode
} from '../types/';

const FILTER_VARIABLE_MAP: FilterOptionVariableMap = {
  [FilterName.Games]: {
    [FilterOptionGame.HoldemNL]: 'HOLDEM',
    [FilterOptionGame.HoldemPL]: 'HOLDEM',
    [FilterOptionGame.HoldemFL]: 'HOLDEM',
    [FilterOptionGame.HoldemShowTime]: 'SHOWTIME_HOLDEM',
    [FilterOptionGame.HoldemSixPlus]: 'SIX_PLUS_HOLDEM',
    [FilterOptionGame.HoldemSplit]: 'SPLIT_HOLDEM',
    [FilterOptionGame.HoldemSwap]: 'SWAP_HOLDEM',
    [FilterOptionGame.AllInPoker]: 'TEMPEST_HOLDEM',
    [FilterOptionGame.HoldemDeepWater]: 'DEEPWATER_HOLDEM',
    [FilterOptionGame.OmahaPL]: 'OMAHA',
    [FilterOptionGame.OmahaNL]: 'OMAHA',
    [FilterOptionGame.Omaha5]: 'OMAHA_5',
    [FilterOptionGame.Omaha6]: 'OMAHA_6',
    [FilterOptionGame.OmahaHiLo]: 'OMAHA',
    [FilterOptionGame.OmahaShowTime]: 'SHOWTIME_OMAHA',
    [FilterOptionGame.OmahaSplit]: 'SPLIT_OMAHA',
    [FilterOptionGame.OmahaCourchevel]: 'COURCHEVEL',
    [FilterOptionGame.OmahaFusion]: 'FUSION',
    [FilterOptionGame.MixedHoldem]: 'MIXED_HOLDEM',
    // TODO: Out of scope for MVP but we need to double check it
    [FilterOptionGame.MixedHoldemNLOmahaPL]: 'MIXED_HOLDEM_OMAHA_PL',
    [FilterOptionGame.MixedHorse]: 'MIXED_HORSE',
    [FilterOptionGame.MixedHose]: 'MIXED_HOSE',
    [FilterOptionGame.Mixed8Game]: 'MIXED_8_GAME',
    [FilterOptionGame.MixedOmaha]: 'MIXED_OMAHA',
    [FilterOptionGame.Stud]: 'STUD',
    [FilterOptionGame.Razz]: 'RAZZ',
    [FilterOptionGame.Draw]: 'DRAW',
    [FilterOptionGame.TripleDraw27]: 'TRIPLE_DRAW_2_7',
    [FilterOptionGame.SingleDraw27]: 'SINGLE_DRAW_2_7',
    [FilterOptionGame.Badugi]: 'BADUGI'
  },
  [FilterName.BuyIn]: {
    [FilterOptionBuyIn.Freeroll]: {
      RM: { minimum: 0, maximum: 1 * FILTER_RANGE_MULTIPLIER },
      PM: { minimum: 0, maximum: 1 * FILTER_RANGE_MULTIPLIER }
    },
    [FilterOptionBuyIn.Micro]: {
      RM: {
        minimum: 1 * FILTER_RANGE_MULTIPLIER,
        maximum: 5 * FILTER_RANGE_MULTIPLIER
      },
      PM: {
        minimum: 2 * FILTER_RANGE_MULTIPLIER,
        maximum: 6 * FILTER_RANGE_MULTIPLIER
      }
    },
    [FilterOptionBuyIn.Low]: {
      RM: {
        minimum: 5 * FILTER_RANGE_MULTIPLIER,
        maximum: 19 * FILTER_RANGE_MULTIPLIER
      },
      PM: {
        minimum: 1 * FILTER_RANGE_MULTIPLIER,
        maximum: 25000 * FILTER_RANGE_MULTIPLIER
      }
    },
    [FilterOptionBuyIn.Medium]: {
      RM: {
        minimum: 20 * FILTER_RANGE_MULTIPLIER,
        maximum: 99 * FILTER_RANGE_MULTIPLIER
      },
      PM: {
        minimum: 25000 * FILTER_RANGE_MULTIPLIER,
        maximum: 249000 * FILTER_RANGE_MULTIPLIER
      }
    },
    [FilterOptionBuyIn.High]: {
      RM: {
        minimum: 100 * FILTER_RANGE_MULTIPLIER,
        maximum: 2500 * FILTER_RANGE_MULTIPLIER
      },
      PM: {
        minimum: 250000 * FILTER_RANGE_MULTIPLIER,
        maximum: 2000000 * FILTER_RANGE_MULTIPLIER
      }
    }
  },
  [FilterName.TableSize]: {
    [FilterOptionTableSize.HeadsUp]: [2],
    [FilterOptionTableSize.Short]: [3, 4, 5, 6],
    [FilterOptionTableSize.Normal]: [7, 8, 9, 10]
  },
  [FilterName.Speed]: {
    [FilterOptionSpeed.Slow]: 'SLOW',
    [FilterOptionSpeed.Normal]: 'REGULAR',
    [FilterOptionSpeed.Turbo]: 'TURBO',
    [FilterOptionSpeed.Hyper]: 'HYPER'
  },
  structure: {
    [FilterOptionGame.HoldemNL]: 'NO_LIMIT',
    [FilterOptionGame.HoldemPL]: 'POT_LIMIT',
    [FilterOptionGame.HoldemFL]: 'FIXED_LIMIT',
    [FilterOptionGame.OmahaPL]: 'POT_LIMIT',
    [FilterOptionGame.OmahaNL]: 'NO_LIMIT'
  }
};

export const mapFilterVariables = (
  filterName: FilterNameType,
  options: OptionToggle[],
  playMode: PlayMode
): FilterVariables => {
  switch (filterName) {
    case FilterName.Games: {
      const games: Game[] = [];
      const structures: Structure[] = [];

      options.forEach(({ value }) => {
        games.push(FILTER_VARIABLE_MAP[filterName][value as FilterOptionGame]);
        const structure = FILTER_VARIABLE_MAP.structure[value as FilterOptionGame];
        if (structure) structures.push(structure);
      });

      return { games, structures };
    }
    case FilterName.BuyIn: {
      const total = { ...FILTER_RANGE_DEFAULT };
      options.forEach(({ value }, index) => {
        const buyInType =
          FILTER_VARIABLE_MAP[filterName][
            value as Exclude<
              FilterOptionBuyIn,
              FilterOptionBuyIn.Custom | FilterOptionBuyIn.Starscoin
            >
          ];
        const range = buyInType && buyInType[playMode];
        if (range) {
          if (!index) {
            total.minimum = range.minimum;
            total.maximum = range.maximum;
          } else {
            total.minimum = total.minimum > range.minimum ? range.minimum : total.minimum;
            total.maximum = total.maximum < range.maximum ? range.maximum : total.maximum;
          }
        }
      });
      return { total };
    }
    case FilterName.TableSize:
      return {
        seats: flatten(
          options.map<number[]>(
            (option) => FILTER_VARIABLE_MAP[filterName][option.value as FilterOptionTableSize]
          )
        )
      };
    case FilterName.Speed:
      return {
        speeds: options.map<Speed>(
          (option) => FILTER_VARIABLE_MAP[filterName][option.value as FilterOptionSpeed]
        )
      };
    default:
      return {};
  }
};

export const mapAllFilterVariables = (activeUiFilters: ActiveUiFilters, playMode: PlayMode) => {
  let mappedFilters = {};
  for (const key in activeUiFilters) {
    if (Object.prototype.hasOwnProperty.call(activeUiFilters, key)) {
      mappedFilters = {
        ...mappedFilters,
        ...mapFilterVariables(
          key as FilterNameType,
          activeUiFilters[key as FilterName] as OptionToggle[],
          playMode
        )
      };
    }
  }
  return mappedFilters;
};

export const mapCustomRangeVariables = (
  filterName: FilterNameType,
  payload: CustomRangePayload
): FilterVariables => {
  switch (filterName) {
    case FilterName.BuyIn: {
      const [min, max] = payload as CustomRangeBuyIn;

      return {
        total: { minimum: min * FILTER_RANGE_MULTIPLIER, maximum: max * FILTER_RANGE_MULTIPLIER }
      };
    }
    default:
      return {};
  }
};

export const isCustomFilterOption = (option: FilterOption): boolean =>
  (option as FilterOptionBase).value === FILTER_OPTION_VALUE_CUSTOM;

export const isGroupFilterOption = (option: FilterOption): option is FilterOptionGroup =>
  (option as FilterOptionGroup).options !== undefined;

export const getBuyInFilterVariableMap = (
  optionValue: Exclude<FilterOptionBuyIn, FilterOptionBuyIn.Custom | FilterOptionBuyIn.Starscoin>,
  playMode: PlayMode
): FiltersBuyInValue => FILTER_VARIABLE_MAP[FilterName.BuyIn][optionValue][playMode];

export const addFilterOptionItemToActiveUiFilters = (
  optionItem: FilterOption,
  activeUiFilters: ActiveUiFilters,
  filterName: FilterName
) => {
  const updatedFilters = { ...activeUiFilters };

  if (isGroupFilterOption(optionItem)) {
    updatedFilters[filterName] = [...(updatedFilters[filterName] || []), ...optionItem.options];
  } else {
    updatedFilters[filterName] = [...(updatedFilters[filterName] || []), optionItem];
  }

  updatedFilters[filterName] = uniqBy(updatedFilters[filterName], 'id');

  return updatedFilters;
};

export const removeFilterOptionItemFromActiveUiFilters = (
  optionItem: FilterOption,
  activeUiFilters: ActiveUiFilters,
  filterName: FilterName
) => {
  const updatedFilters = { ...activeUiFilters };
  if (isGroupFilterOption(optionItem)) {
    updatedFilters[filterName] = (updatedFilters[filterName] || []).filter(
      (selected) => !optionItem.options.map((item) => item.id).includes(selected.id)
    );
  } else {
    updatedFilters[filterName] = (updatedFilters[filterName] || []).filter(
      (selected) => selected.value !== optionItem.value
    );
  }

  return updatedFilters;
};

export const isOptionItemChecked = (
  optionItem: FilterOption,
  activeUiFilters: ActiveUiFilters,
  filterName: FilterName
) => {
  if (!activeUiFilters[filterName]) {
    return false;
  }

  if (isGroupFilterOption(optionItem)) {
    return optionItem.options.every((item) => (activeUiFilters[filterName] || []).includes(item));
  }

  return (activeUiFilters[filterName] || []).map((item) => item.id).includes(optionItem.id);
};

const getAllActiveUiFiltersCount = (activeUiFilters: ActiveUiFilters) => {
  const activeUiFiltersCount = Object.keys(activeUiFilters).reduce(
    (accumulator, currentValue) =>
      accumulator + (activeUiFilters[currentValue as FilterName] || []).length,
    0
  );

  return activeUiFiltersCount;
};

export const hasAnyActiveUiFilters = (activeUiFilters: ActiveUiFilters, filterName?: FilterName) =>
  filterName
    ? !!(activeUiFilters[filterName] || []).length
    : !!getAllActiveUiFiltersCount(activeUiFilters);
